import { Button, Modal } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Page from '../../common/Page'
import PageCard from '../../common/PageCard'
import Actions from '../../../actions'
import $t from '../../../i18n'
import config from '../../../config.json'
import { isMemberTermed } from '../../../utils/hmoUtils'
import SubscriptionCard from '../../common/SubscriptionCard'
import PaymentDetailsCard from '../../common/PaymentDetailsCard'

import './ManagePayments.css'

const ManagePayments = () => {
  const dispatch = useDispatch()

  const zouraIframeRef = useRef()
  const paymentDetailsCardRef = useRef()
  const userDetails = useSelector(state => state.profile.userDetails)
  const paymentDetailsData = useSelector(
    state => state.payment.paymentDetailsData
  )
  const isPaymentDetailsLoading = useSelector(
    state => state.payment.isPaymentDetailsLoading
  )

  const paymentAddressObj = useSelector(
    state => state.payment.paymentAddressObj
  )

  const isPaymentDetailsFailed = useSelector(
    state => state.payment.isPaymentDetailsFailed
  )

  const updatingBillingDialogLoader = useSelector(
    state => state.payment.updatingBillingDialogLoader
  )

  const [isEditableView, setIsEditableView] = useState(false)
  const [isAutoPaySetupMode, setIsAutoPaySetupMode] = useState(false)
  const [isSwitchToOneTimeMode, setIsSwitchToOneTimeMode] = useState(false)
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false)
  const [isSwitchInfoModalOpen, setIsSwitchInfoModalOpen] = useState(false)

  const prevUpdatingRef = useRef(false)
  useEffect(() => {
    if (
      prevUpdatingRef.current &&
      !updatingBillingDialogLoader &&
      isEditableView
    ) {
      setIsEditableView(false)
      setIsAutoPaySetupMode(false)
      setIsSwitchToOneTimeMode(false)
    }
    prevUpdatingRef.current = updatingBillingDialogLoader
  }, [updatingBillingDialogLoader])

  const isAutoPay = paymentDetailsData?.accountSummary?.isAutoPay
  const isPaymentMethodAvailable = !!(
    paymentDetailsData?.paymentMethod?.cardNumber ||
    paymentDetailsData?.paymentMethod?.bankAccountNumber
  )

  const shouldShowFooterAction = !isMemberTermed(userDetails)

  const onEditPaymentClick = () => {
    setIsEditableView(true)

    dispatch(
      Actions.createAction(Actions.GENERATE_UPDATE_PAYMENT_CARD_REQUESTED)
    )
  }

  const onConfirmAutoPayClick = () => {
    if (isPaymentMethodAvailable) {
      dispatch(Actions.createAction(Actions.CONFIRM_AUTO_PAY_CLICKED))
    } else {
      // no payment info — open the form to collect it first
      setIsAutoPaySetupMode(true)
      if (!isEditableView) {
        onEditPaymentClick()
      } else {
        paymentDetailsCardRef.current?.submitDetails()
      }
    }
  }

  const onUpdatePaymentMethodSuccess = data => {
    const skipSuccessMessage = isAutoPaySetupMode || isSwitchToOneTimeMode
    let cardData = {
      defaultPaymentMethodId: data.refId,
      ...paymentAddressObj,
      skipSuccessMessage
    }
    dispatch(
      Actions.createAction(
        Actions.PAYMENT_METHOD_CARD_UPDATE_REQUESTED,
        cardData
      )
    )
    if (isAutoPaySetupMode) {
      dispatch(
        Actions.createAction(Actions.SET_AUTO_PAY_REQUESTED, {
          autoPay: true,
          setNewPaymentMethod: true,
          paymentMethodId: data.refId,
          paymentMethodType: data.paymentMethodType
        })
      )
    }
    if (isSwitchToOneTimeMode) {
      dispatch(
        Actions.createAction(Actions.SET_AUTO_PAY_REQUESTED, {
          autoPay: false,
          setNewPaymentMethod: false
        })
      )
    }
    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        eventName: Actions.ADD_PAYMENT_METHOD_SUCCEED
      })
    )
  }

  return (
    <PageCard enableBack={true} className='manage-payments-page-card'>
      <Page
        title={$t('pages.paymentsAndBilling.manage.title')}
        className='manage-payments-page'
        hideAlertBanner
        loading={isPaymentDetailsLoading}
      >
        {isPaymentDetailsFailed && (
          <img
            className='billing-error-img'
            src={config.gcpStorageUrl + '/img/billing-error.png'}
            alt=''
          />
        )}
        {!isPaymentDetailsFailed && (
          <>
            {' '}
            <div className='body-container'>
              <SubscriptionCard paymentDetails={paymentDetailsData} />

              <PaymentDetailsCard
                actionRef={paymentDetailsCardRef}
                isEditableView={isEditableView}
                onEdit={onEditPaymentClick}
                zouraIframeRef={zouraIframeRef}
                onSuccess={onUpdatePaymentMethodSuccess}
                isManagePayment={true}
                onCancel={() => {
                  setIsEditableView(false)
                  setIsAutoPaySetupMode(false)
                  setIsSwitchToOneTimeMode(false)
                }}
                SubmitButtonText={$t('pages.billing.continuePayment')}
              />
              {isAutoPay && isEditableView && (
                <div className='switch-to-one-time-wrapper'>
                  <Button
                    type='link'
                    onClick={() => setIsSwitchModalOpen(true)}
                  >
                    {$t('pages.paymentsAndBilling.switchToOneTime')}
                  </Button>
                </div>
              )}
              <Modal
                open={isSwitchModalOpen}
                footer={null}
                closable={false}
                centered
                wrapClassName='manage-payments-switch-modal'
              >
                <div className='switch-modal-content'>
                  <h3>{$t('pages.paymentsAndBilling.switchModalTitle')}</h3>
                  <p>{$t('pages.paymentsAndBilling.switchModalDesc')}</p>
                  <Button
                    type='default'
                    className='switch-modal-yes'
                    onClick={() => {
                      setIsSwitchModalOpen(false)
                      setIsSwitchInfoModalOpen(true)
                    }}
                  >
                    {$t('pages.paymentsAndBilling.switchModalConfirm')}
                  </Button>
                  <Button
                    type='primary'
                    className='switch-modal-keep'
                    onClick={() => setIsSwitchModalOpen(false)}
                  >
                    {$t('pages.paymentsAndBilling.switchModalCancel')}
                  </Button>
                </div>
              </Modal>
              <Modal
                open={isSwitchInfoModalOpen}
                footer={null}
                closable={false}
                centered
                wrapClassName='manage-payments-switch-modal'
              >
                <div className='switch-modal-content'>
                  <h3>{$t('pages.paymentsAndBilling.switchInfoModalTitle')}</h3>
                  <p>{$t('pages.paymentsAndBilling.switchInfoModalDesc')}</p>
                  <Button
                    type='default'
                    className='switch-modal-yes'
                    onClick={() => {
                      setIsSwitchInfoModalOpen(false)
                      setIsSwitchToOneTimeMode(true)
                      paymentDetailsCardRef.current?.submitDetails()
                    }}
                  >
                    {$t('pages.paymentsAndBilling.switchInfoModalConfirm')}
                  </Button>
                  <Button
                    type='primary'
                    className='switch-modal-keep'
                    onClick={() => setIsSwitchInfoModalOpen(false)}
                  >
                    {$t('pages.paymentsAndBilling.switchInfoModalCancel')}
                  </Button>
                </div>
              </Modal>
            </div>
            {shouldShowFooterAction && !isAutoPay && (
              <div className='page-footer'>
                <div className='payment-card'>
                  <div className='auto-pay-desc'>
                    {$t('pages.paymentsAndBilling.autoPayDesc')}
                  </div>
                  <Button
                    type='primary'
                    className='auto-pay-button'
                    onClick={onConfirmAutoPayClick}
                    disabled={isEditableView}
                  >
                    {$t('pages.paymentsAndBilling.confirmAutoPay')}
                  </Button>
                  <div className='auto-pay-note'>
                    {$t('pages.paymentsAndBilling.autoPayNote')}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </Page>
    </PageCard>
  )
}
export default ManagePayments
